import React, { useEffect, useRef, useState } from 'react';
import { User, Affiliate, NotificationPrivacy, PresenceManual, PresenceStatus, handleAvatarError } from '../types';
import { toggleKbDebug } from './KeyboardViewportDebug';
import { resolveAvatarUrl } from '../utils/serverUrl';
import { authService } from '../services/authService';
import { getAppVersion } from '../services/appVersion';
import { isTauri } from '../utils/tauri';
import { IS_COARSE_POINTER } from '../utils/pointer';
import { Bell, BellRing, ScrollText, HelpCircle, Shield, Info, ChevronRight, LogOut, Pencil, Monitor, RotateCw, UserCheck, Smartphone } from 'lucide-react';
import { MyDevicesModal } from './MyDevicesModal';
import { refreshApp } from '../utils/appRefresh';
import { Modal } from './Modal';
import { NotificationSettingsPanel } from './notifications/NotificationSettingsPanel';
import { NotificationPrivacySetting } from './notifications/NotificationPrivacySetting';
import { normalizeNotificationPrivacy } from '../utils/notificationPrivacy';
import { useNotificationPermission } from '../hooks/useNotificationPermission';
import { describePushStatus } from '../utils/pushSupport';
import { isWebNotifyEnabled } from '../utils/webNotifyPref';
import { PresenceDot } from './PresenceDot';
import { PresenceStatusSetting } from './PresenceStatusSetting';
import { STATUS_LABEL } from '../utils/presence';

interface MobileSettingsScreenProps {
  currentUser: User;
  activityUnread?: number;
  onEditProfile: () => void;
  /** 알림 내용 공개 수준을 로컬에 반영한다(서버 저장은 NotificationPrivacySetting이 이미 끝냈다). */
  onNotificationPrivacyChange: (privacy: NotificationPrivacy) => void;
  /** 서버가 판정한 내 접속 상태 — 프로필 카드 아바타의 점 + '내 상태' 행의 현재 값 */
  myStatus: PresenceStatus;
  /** 내가 직접 고른 상태(null=자동). 선택 모달의 체크 위치 */
  myPresenceManual: PresenceManual;
  /** 수동 상태를 로컬에 반영한다(서버 저장은 PresenceStatusSetting이 이미 끝냈다). */
  onPresenceManualChange: (manual: PresenceManual) => void;
  onActivityClick?: () => void;
  onRulesClick?: () => void;
  onHelpClick?: () => void;
  onAdminConsoleClick?: () => void;
  onLogout: () => void;
}

/**
 * 모바일 '설정' 탭.
 * 홈 퀵액션에 흩어져 있던 도움말·관리자·로그아웃과 프로필 카드의 톱니바퀴를 한 화면으로 모은다.
 * 데스크톱은 rail 아바타 팝오버가 같은 역할을 하므로 이 화면은 모바일에서만 마운트된다.
 */
export const MobileSettingsScreen: React.FC<MobileSettingsScreenProps> = ({
  currentUser,
  activityUnread = 0,
  onEditProfile,
  onNotificationPrivacyChange,
  myStatus,
  myPresenceManual,
  onPresenceManualChange,
  onActivityClick,
  onRulesClick,
  onHelpClick,
  onAdminConsoleClick,
  onLogout,
}) => {
  const [affiliateLabel, setAffiliateLabel] = useState<string | null>(null);
  const [version, setVersion] = useState<string>('');
  const [isNotifyOpen, setIsNotifyOpen] = useState(false);
  const [isPresenceOpen, setIsPresenceOpen] = useState(false);
  const [isDevicesOpen, setIsDevicesOpen] = useState(false);
  const { permission, capability } = useNotificationPermission();
  // 렌더 시점에 플래그를 읽는다 — 모달 안 토글이 바뀌어도 모달을 닫는 setState가
  // 이 컴포넌트를 리렌더하므로 trailing 칩이 따라온다 (별도 구독 불필요)
  const pushStatus = describePushStatus(capability, permission, isWebNotifyEnabled());

  useEffect(() => {
    let cancelled = false;
    if (currentUser.affiliateId) {
      authService.getAffiliates()
        .then((list: Affiliate[]) => {
          if (cancelled) return;
          const match = list.find(a => a.id === currentUser.affiliateId);
          if (match) setAffiliateLabel(match.label);
        })
        .catch(() => { });
    }
    getAppVersion().then(v => { if (!cancelled) setVersion(v); }).catch(() => { });
    return () => { cancelled = true; };
  }, [currentUser.affiliateId]);

  // 숨은 진단 토글: '앱 정보'를 3초 안에 7번 탭하면 키보드 뷰포트 진단 오버레이가 켜진다.
  // PWA에는 주소창이 없어 ?kbdebug 쿼리를 쓸 수 없고 iOS Web Inspector는 맥 전용이라,
  // Windows 개발 환경에서는 실기기 화면에 수치를 띄우는 것이 유일한 진단 수단이다.
  const aboutTapRef = useRef({ count: 0, at: 0 });
  const handleAboutTap = () => {
    const now = Date.now();
    const s = aboutTapRef.current;
    s.count = now - s.at < 3000 ? s.count + 1 : 1;
    s.at = now;
    if (s.count >= 7) {
      s.count = 0;
      toggleKbDebug();
    }
  };

  const rows: Array<{ key: string; label: string; icon: React.ElementType; onClick?: () => void; badge?: number; trailing?: string } | false> = [
    // 내 상태 — 계정 단위 설정이라 Tauri에서도 보인다(기기별인 '알림 설정'과 다르다)
    { key: 'presence', label: '내 상태', icon: UserCheck, onClick: () => setIsPresenceOpen(true), trailing: STATUS_LABEL[myStatus] },
    !!onActivityClick && { key: 'activity', label: '알림함', icon: Bell, onClick: onActivityClick, badge: activityUnread },
    // 알림 켜기/차단 해제 진입점. 아이콘을 Bell로 두면 바로 위 '알림함'과 구분되지 않아 BellRing을 쓴다.
    // Tauri는 트레이의 "알림 받기"가 최상위 스위치라 이 행을 띄우지 않는다.
    !isTauri() && { key: 'push', label: '알림 설정', icon: BellRing, onClick: () => setIsNotifyOpen(true), trailing: pushStatus.label },
    // 기기 신뢰 — 이 기기 승인 요청·확인 코드, 분실 기기 해제
    { key: 'devices', label: '내 기기', icon: Smartphone, onClick: () => setIsDevicesOpen(true) },
    !!onRulesClick && { key: 'rules', label: '채팅 이용 안내', icon: ScrollText, onClick: onRulesClick },
    !!onHelpClick && { key: 'help', label: '도움말', icon: HelpCircle, onClick: onHelpClick },
    // PC 앱에서는 이미 설치된 상태라 숨긴다. 설치파일은 로그인한 사용자만 받을 수 있어
    // 로그인 화면이 아니라 여기가 유일한 진입점이다 (새 탭이라 대화 세션이 유지된다).
    // 터치 기기(폰·태블릿)에서는 Windows 설치파일을 실행할 수 없어 숨긴다 — 데스크톱 rail도 같은 게이트.
    !isTauri() && !IS_COARSE_POINTER && { key: 'download', label: 'PC 버전 다운로드', icon: Monitor, onClick: () => window.open('/download', '_blank') },
    !!(currentUser.isAdmin && onAdminConsoleClick) && { key: 'admin', label: '관리자 콘솔', icon: Shield, onClick: onAdminConsoleClick },
    // 홈 화면에 설치한 PWA에는 주소창이 없고 당김 새로고침도 막혀 있어(index.css의 overscroll-behavior),
    // 목록 상단 당김 제스처를 모르는 사용자를 위한 두 번째 진입점. Tauri는 창 자체가 앱이라 숨긴다.
    !isTauri() && { key: 'refresh', label: '새로고침', icon: RotateCw, onClick: () => { void refreshApp(); } },
    { key: 'about', label: '앱 정보', icon: Info, trailing: version === 'web' ? '웹 버전' : version ? `v${version}` : '' },
  ];

  const card = 'bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden';

  return (
    <div className="h-full overflow-y-auto bg-slate-50 safe-area-top">
      <div className="px-4 pt-3 pb-8 space-y-5">
        <h1 className="text-xl font-extrabold text-slate-800 tracking-tight pt-1">설정</h1>

        {/* 프로필 */}
        <button
          type="button"
          onClick={onEditProfile}
          className={`${card} w-full flex items-center gap-3.5 px-4 py-4 text-left active:bg-slate-50 transition-colors`}
        >
          <span className="relative shrink-0">
            <img
              src={resolveAvatarUrl(currentUser.avatar)}
              onError={handleAvatarError}
              alt=""
              className="w-14 h-14 rounded-full object-cover bg-slate-100"
            />
            <PresenceDot
              status={myStatus}
              labelled
              className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 border-2 border-white"
            />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-base font-bold text-slate-800 truncate">{currentUser.username}</span>
            <span className="block text-xs text-slate-500 truncate">
              {[affiliateLabel, currentUser.depart].filter(Boolean).join(' · ') || ' '}
            </span>
          </span>
          <span className="flex items-center gap-1 text-xs font-semibold text-brand-700 shrink-0">
            <Pencil size={14} aria-hidden="true" />
            프로필 수정
          </span>
        </button>

        {/* 메뉴 */}
        <div className={`${card} divide-y divide-slate-100`}>
          {rows.filter((r): r is Exclude<typeof r, false> => !!r).map(row => {
            const Icon = row.icon;
            const content = (
              <>
                <span className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
                  <Icon size={18} aria-hidden="true" />
                </span>
                <span className="flex-1 text-[15px] text-slate-800">{row.label}</span>
                {row.badge ? (
                  <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-brand-600 text-white text-[11px] font-bold flex items-center justify-center">
                    {row.badge > 99 ? '99+' : row.badge}
                  </span>
                ) : null}
                {row.trailing ? <span className="text-sm text-slate-400">{row.trailing}</span> : null}
                {row.onClick && <ChevronRight size={18} className="text-slate-300 shrink-0" aria-hidden="true" />}
              </>
            );
            return row.onClick ? (
              <button
                key={row.key}
                type="button"
                onClick={row.onClick}
                className="w-full min-h-[56px] flex items-center gap-3 px-4 py-2.5 text-left active:bg-slate-50 transition-colors"
              >
                {content}
              </button>
            ) : (
              <div
                key={row.key}
                className="w-full min-h-[56px] flex items-center gap-3 px-4 py-2.5"
                // '앱 정보'만 숨은 진단 토글(7연타)을 받는다 — 버튼이 아니라 chevron 없는 정보 행 그대로 둔다
                onClick={row.key === 'about' ? handleAboutTap : undefined}
              >
                {content}
              </div>
            );
          })}
        </div>

        {/* 로그아웃 — 파괴적 액션은 아웃라인 */}
        <button
          type="button"
          onClick={onLogout}
          className="w-full h-12 rounded-2xl bg-white border border-brand-600 text-brand-600 font-semibold flex items-center justify-center gap-2 hover:bg-brand-50 active:bg-brand-100 transition-colors"
        >
          <LogOut size={18} aria-hidden="true" />
          로그아웃
        </button>
      </div>

      <MyDevicesModal isOpen={isDevicesOpen} onClose={() => setIsDevicesOpen(false)} currentUser={currentUser} />

      <Modal isOpen={isPresenceOpen} onClose={() => setIsPresenceOpen(false)} title="내 상태" size="sm">
        <PresenceStatusSetting value={myPresenceManual} onChange={onPresenceManualChange} />
      </Modal>

      <Modal isOpen={isNotifyOpen} onClose={() => setIsNotifyOpen(false)} title="알림 설정" size="sm">
        <NotificationSettingsPanel />
        {/* 위 스위치는 이 기기 전용이고, 아래 설정은 계정 전체에 적용된다 */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <NotificationPrivacySetting
            value={normalizeNotificationPrivacy(currentUser.notificationPrivacy)}
            onChange={onNotificationPrivacyChange}
          />
        </div>
      </Modal>
    </div>
  );
};

export default MobileSettingsScreen;
